import React, { useEffect, useMemo, useState } from 'react';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ShieldCheck, Search, Save, AlertTriangle, Users, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { StaffPermissionToggles } from '@/components/StaffPermissionToggles';

export default function QAAdmin() {
  const [data, setData] = useState({ qa_users: [], tenants: [], surveys_by_tenant: {}, available_permissions: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user_id currently editing
  const [draftTenantIds, setDraftTenantIds] = useState([]);
  const [draftSurveyIds, setDraftSurveyIds] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('');
  const [saving, setSaving] = useState(false);

  // Add-reviewer modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/qa/admin/qa-users`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) { toast.error(d?.detail || 'Could not load QA users'); return; }
      setData({
        qa_users: d.qa_users || [],
        tenants: d.tenants || [],
        surveys_by_tenant: d.surveys_by_tenant || {},
        available_permissions: d.available_permissions || {},
      });
    } catch { toast.error('Could not load QA users'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (u) => {
    setEditing(u.user_id);
    setDraftTenantIds([...(u.assigned_tenant_ids || [])]);
    setDraftSurveyIds([...(u.assigned_survey_ids || [])]);
    setTenantFilter('');
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraftTenantIds([]);
    setDraftSurveyIds([]);
    setTenantFilter('');
  };
  const toggleTenant = (tid) => {
    setDraftTenantIds(prev => {
      if (prev.includes(tid)) {
        // Also strip survey-level restrictions belonging to this tenant.
        const surveysHere = (data.surveys_by_tenant[tid] || []).map(s => s.survey_id);
        setDraftSurveyIds(sp => sp.filter(sid => !surveysHere.includes(sid)));
        return prev.filter(x => x !== tid);
      }
      return [...prev, tid];
    });
  };
  const toggleSurvey = (sid) => {
    setDraftSurveyIds(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/qa/admin/qa-users/${editing}/assigned-tenants`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_ids: draftTenantIds, survey_ids: draftSurveyIds }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d?.detail || 'Save failed'); return; }
      toast.success('QA reviewer scope saved');
      setData(prev => ({
        ...prev,
        qa_users: prev.qa_users.map(u =>
          u.user_id === editing
            ? { ...u,
                assigned_tenant_ids: d.assigned_tenant_ids || [],
                assigned_survey_ids: d.assigned_survey_ids || [] }
            : u
        ),
      }));
      cancelEdit();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const togglePermission = async (u, perm, enabled) => {
    const current = u.permissions || [];
    const next = enabled ? [...new Set([...current, perm])] : current.filter(p => p !== perm);
    try {
      const res = await fetch(`${API}/qa/admin/qa-users/${u.user_id}/permissions`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d?.detail || 'Could not update permission'); return; }
      setData(prev => ({
        ...prev,
        qa_users: prev.qa_users.map(x => x.user_id === u.user_id ? { ...x, permissions: d.permissions || [] } : x),
      }));
      toast.success(enabled ? 'Permission granted' : 'Permission removed');
    } catch { toast.error('Could not update permission'); }
  };

  const invite = async () => {
    const name = inviteName.trim();
    const email = inviteEmail.trim().toLowerCase();
    if (!name) { toast.error('Name is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email'); return; }
    setInviting(true);
    try {
      const res = await fetch(`${API}/users/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, role: 'qa' }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d?.detail || 'Could not add QA reviewer'); return; }
      toast.success('QA reviewer invited. They will get an email to set their password.');
      setInviteOpen(false);
      setInviteName('');
      setInviteEmail('');
      // Refresh list so the new pending reviewer appears.
      await load();
    } catch { toast.error('Could not add QA reviewer'); }
    finally { setInviting(false); }
  };

  const tenantsFiltered = data.tenants.filter(t => {
    if (!tenantFilter.trim()) return true;
    const q = tenantFilter.toLowerCase();
    return (t.name || '').toLowerCase().includes(q) || (t.tenant_id || '').toLowerCase().includes(q);
  });
  const tenantNameById = useMemo(
    () => Object.fromEntries(data.tenants.map(t => [t.tenant_id, t.name || t.tenant_id])),
    [data.tenants]
  );
  // For read-only rendering of a reviewer's survey chips.
  const surveyNameById = useMemo(() => {
    const out = {};
    for (const list of Object.values(data.surveys_by_tenant || {})) {
      for (const s of list) out[s.survey_id] = s.name;
    }
    return out;
  }, [data.surveys_by_tenant]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qa-admin-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-[#0EA5E9]" />
            <div>
              <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A]">QA Reviewers</h1>
              <p className="text-sm text-[#64748B]">
                Assign tenants (and optionally specific surveys within each tenant) that each QA reviewer can review.
                QA users only see FINDs — and receive email alerts — for the surveys within their assigned scope.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setInviteOpen(true)}
            className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
            data-testid="qa-add-reviewer-btn"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> Add QA Reviewer
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#64748B]" data-testid="qa-admin-loading">Loading…</div>
        ) : data.qa_users.length === 0 ? (
          <div className="bg-white border border-dashed border-[#CBD5E1] rounded-xl p-10 text-center" data-testid="qa-admin-empty">
            <Users className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
            <p className="text-sm text-[#64748B]">No QA reviewers yet. Use <span className="font-semibold">Add QA Reviewer</span> to invite one.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.qa_users.map(u => {
              const isEditing = editing === u.user_id;
              const assignedTenants = u.assigned_tenant_ids || [];
              const assignedSurveys = u.assigned_survey_ids || [];
              const tenantCount = isEditing ? draftTenantIds.length : assignedTenants.length;
              return (
                <div key={u.user_id} className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid={`qa-user-${u.email}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-[#0F172A]">{u.name || u.email}</p>
                      <p className="text-xs text-[#64748B]">{u.email}</p>
                      {u.status === 'pending' && (
                        <p className="text-[11px] text-amber-700 mt-0.5">Pending — waiting for first login</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${tenantCount === 0 ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'}`}>
                        {tenantCount} tenant{tenantCount === 1 ? '' : 's'} assigned
                      </Badge>
                      {!isEditing ? (
                        <Button size="sm" variant="outline" onClick={() => startEdit(u)} data-testid={`qa-edit-${u.email}`}>Manage</Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>Cancel</Button>
                          <Button size="sm" onClick={save} disabled={saving} className="bg-[#1A3E6F] hover:bg-[#15325A] text-white" data-testid={`qa-save-${u.email}`}>
                            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {tenantCount === 0 && !isEditing && (
                    <p className="mt-2 text-xs text-amber-700 inline-flex items-center gap-1.5" data-testid={`qa-warning-no-tenants-${u.email}`}>
                      <AlertTriangle className="w-3 h-3" /> No tenants assigned — this reviewer sees an empty queue and receives no alerts.
                    </p>
                  )}

                  <StaffPermissionToggles user={u} available={data.available_permissions} onToggle={togglePermission} />

                  {!isEditing && assignedTenants.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {assignedTenants.map(tid => {
                        const surveysForTenant = (data.surveys_by_tenant[tid] || []).map(s => s.survey_id);
                        const restricted = assignedSurveys.filter(sid => surveysForTenant.includes(sid));
                        return (
                          <div key={tid} className="flex items-start gap-2 flex-wrap">
                            <span className="text-xs bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE] px-2 py-0.5 rounded">
                              {tenantNameById[tid] || tid}
                            </span>
                            {restricted.length === 0 ? (
                              <span className="text-[11px] text-[#94A3B8]">all surveys</span>
                            ) : (
                              <span className="flex flex-wrap gap-1">
                                {restricted.map(sid => (
                                  <span key={sid} className="text-[11px] bg-amber-50 text-amber-900 border border-amber-200 px-1.5 py-0.5 rounded">
                                    {surveyNameById[sid] || sid}
                                  </span>
                                ))}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {isEditing && (
                    <div className="mt-4 border-t border-[#F1F5F9] pt-3 space-y-3">
                      <div className="relative max-w-xs">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#94A3B8]" />
                        <Input
                          placeholder="Filter tenants…"
                          value={tenantFilter}
                          onChange={(e) => setTenantFilter(e.target.value)}
                          className="pl-8 h-9 text-sm"
                          data-testid="qa-tenant-filter"
                        />
                      </div>
                      <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
                        {tenantsFiltered.length === 0 ? (
                          <p className="text-xs text-[#94A3B8]">No tenants match</p>
                        ) : tenantsFiltered.map(t => {
                          const checked = draftTenantIds.includes(t.tenant_id);
                          const surveys = data.surveys_by_tenant[t.tenant_id] || [];
                          return (
                            <div
                              key={t.tenant_id}
                              className={`p-2 rounded border text-sm transition-colors ${
                                checked ? 'bg-emerald-50 border-emerald-300' : 'border-[#E2E8F0]'
                              }`}
                              data-testid={`tenant-${t.tenant_id}`}
                            >
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleTenant(t.tenant_id)}
                                  data-testid={`tenant-check-${t.tenant_id}`}
                                />
                                <span className="text-[#0F172A] font-medium truncate">{t.name || t.tenant_id}</span>
                              </label>

                              {/* Optional survey-level restrictions — only when tenant is picked */}
                              {checked && surveys.length > 0 && (
                                <div
                                  className="mt-2 pl-6 space-y-1"
                                  data-testid={`survey-restriction-list-${t.tenant_id}`}
                                >
                                  <p className="text-[11px] text-[#64748B]">
                                    Optional: restrict to specific surveys/projects.
                                    Leave all unchecked to allow every survey of this tenant.
                                  </p>
                                  {surveys.map(s => {
                                    const sChecked = draftSurveyIds.includes(s.survey_id);
                                    return (
                                      <label
                                        key={s.survey_id}
                                        className="flex items-center gap-2 text-xs cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={sChecked}
                                          onChange={() => toggleSurvey(s.survey_id)}
                                          data-testid={`survey-check-${s.survey_id}`}
                                        />
                                        <span className="text-[#0F172A]">{s.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {checked && surveys.length === 0 && (
                                <p className="pl-6 mt-1 text-[11px] text-[#94A3B8]">
                                  No surveys yet for this tenant — reviewer will see all surveys as they are created.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-[#94A3B8]">
                        {draftTenantIds.length} tenant{draftTenantIds.length === 1 ? '' : 's'} · {draftSurveyIds.length} survey restriction{draftSurveyIds.length === 1 ? '' : 's'} · changes apply on save
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add QA Reviewer modal — reuses /users/invite (role=qa). */}
      <Dialog open={inviteOpen} onOpenChange={(v) => !inviting && setInviteOpen(v)}>
        <DialogContent data-testid="qa-add-reviewer-dialog">
          <DialogHeader>
            <DialogTitle>Add QA Reviewer</DialogTitle>
            <DialogDescription>
              An invite email will be sent so they can set their password on first login. The reviewer is created with role <span className="font-mono">qa</span> and will appear in this list immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-[#475569]">Name</label>
              <Input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Full name"
                disabled={inviting}
                data-testid="qa-add-reviewer-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#475569]">Email</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="reviewer@example.com"
                disabled={inviting}
                data-testid="qa-add-reviewer-email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              disabled={inviting}
              data-testid="qa-add-reviewer-cancel"
            >Cancel</Button>
            <Button
              onClick={invite}
              disabled={inviting}
              className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
              data-testid="qa-add-reviewer-submit"
            >{inviting ? 'Sending invite…' : 'Send invite'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
